import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Typography,
  useTheme,
} from "@material-ui/core";
import ibanLib from "iban";
import { ThemedAsset, assetIds } from "nemuru-components";
import React, { useContext, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { CONTACT_INFO_BY_REGISTRY } from "../../../../config/languages";
import { ConfigContext } from "../../../../contexts/ConfigContext";
import { ProcessContext } from "../../../../contexts/ProcessContext";
import { fakeFormsDataIncome } from "../../../../utils/fakeFormsData";
import { useStepHandler } from "../../../../utils/hooks/useStepHandler";
import { DeclaredIncomeDataMapper } from "../../../../utils/mappers/DeclaredIncomeDataMapper";
import { PatchAddBorrowerCashInCashOutMethod } from "../../../../utils/mappers/PatchAddBorrowerCashInCashOutMethod";
import { PatchSetBorrowerCashInCashOutMethodToLoan } from "../../../../utils/mappers/PatchSetBorrowerCashInCashOutMethodToLoan";
import BackButton from "../../../shared/components/BackButton";
import ResponsiveLayout from "../../../shared/components/ResponsiveLayout";
import Text from "../../../shared/components/Text";
import { FLOW_MESSAGES } from "../../../shared/components/flowMessages/components";
import {
  ONBOARDING_PARTICIPANT_ROLES,
  STEP_GROUP_IDS,
} from "../../../shared/config";
import SetBankAccount from "../shared/steps/SetBankAccount";
import {
  DECLARED_INCOME_ON_CONDITIONS,
  isSetBankAccountStepAllowedByRole,
} from "./config";
import NoIncomeMessage from "./shared/components/NoIncomeMessage";
import EditIncomeForm from "./shared/steps/EditIncomeForm";

const TIMEOUT_MILLISECONDS = 1000;
const MAX_TIMEOUT_MILLISECONDS = 4000;

export default function DeclaredIncomeOnConditions(props) {
  const {
    state: {
      loan,
      lastActionsReferenceLastChange,
      lastActionsReference,
      income,
      documentationRequired,
      participant,
    }, //income comes from REQUEST_GET_DECLARED_INCOME
    dispatch,
  } = useContext(ProcessContext);

  const { disableNetIncomeType, disableOthers, allowInternationalBorrower } =
    props;
  const { palette } = useTheme();

  const STEP_GROUP_STEPS_ARRAY = [
    {
      id: DECLARED_INCOME_ON_CONDITIONS.EDIT_INCOME_FORM,
      show: () => true,
    },
    {
      id: DECLARED_INCOME_ON_CONDITIONS.SET_BANK_ACCOUNT,
      show: () => isSetBankAccountStepAllowedByRole(participant),
    },
  ];
  const CURRENT_STEP_GROUP = STEP_GROUP_IDS.DeclaredIncomeOnConditions;

  const employmentStatus =
    loan?.borrower_user_details_on_origination?.professional_details
      ?.employment_status;

  const [state, setState] = useState({
    values: {},
    formSubmitSuccess: false,
    bankAccountIBAN: loan.borrower_cash_in_cash_out_method_account_number,
    borrowerCashInCashOutMethodId: null,
    declaredIncomeId: null,
    timer: TIMEOUT_MILLISECONDS,
    noIncomeMessage: true,
    isReady: false,
    cashInCashOutMethodAccountNumberIsNull: undefined,
  });

  const {
    values,
    noIncomeMessage,
    timer,
    isReady,
    cashInCashOutMethodAccountNumberIsNull,
  } = state;

  const onSubmitModule = (values) => {
    const declaredIncomeId = uuidv4();

    setState((s) => ({
      ...s,
      declaredIncomeId: declaredIncomeId,
      bankAccountIBAN: values?.bankAccountIBAN
        ? ibanLib.electronicFormat(values.bankAccountIBAN)
        : undefined,
      values: values,
    }));
    const dehydratedData = DeclaredIncomeDataMapper.hydrate({
      ...values,
      id: declaredIncomeId,
      loanId: loan.id,
      borrowerId: participant.participantId,
    });
    dispatch({
      type: "REQUEST_POST_DECLARED_INCOME",
      payload: dehydratedData,
    });
    dispatch({
      type: "SET_FLOW_LOADING",
      payload: { show: true },
    });
  };

  const currentModuleStepHandler = useStepHandler(
    onSubmitModule,
    fakeFormsDataIncome
  );

  const { manageGroup, submitStep, onBack, backButtonVisible, getStepData } =
    currentModuleStepHandler;

  // Avoid sending step progress event if module is eventually skipped
  const stepGroupStepToShow =
    isReady && manageGroup(CURRENT_STEP_GROUP, STEP_GROUP_STEPS_ARRAY);

  useEffect(() => {
    dispatch({
      type: "SET_FLOW_LOADING",
      payload: { show: true },
    });
  }, []);

  useEffect(() => {
    onConditions();
  }, [timer]);

  const onConditions = () => {
    if (
      timer > MAX_TIMEOUT_MILLISECONDS &&
      loan.borrower_cash_in_cash_out_method_account_number === null
    ) {
      onShowProcessCriticalError();
    } else {
      if (!documentationRequired === undefined) {
        dispatch({
          type: "REQUEST_GET_DOCUMENTATION_REQUIRED",
          payload: {
            loanId: loan.id,
            borrowerId: participant.participantId,
          },
        });
      }
      if (
        (loan.borrower_cash_in_cash_out_method_account_number &&
          income?.valid === true &&
          participant.role === ONBOARDING_PARTICIPANT_ROLES.BORROWER) ||
        (income?.valid === true &&
          participant.role !== ONBOARDING_PARTICIPANT_ROLES.BORROWER)
      ) {
        onSkipModule();
      }
      if (
        loan.borrower_cash_in_cash_out_method_account_number &&
        income?.valid === false
      ) {
        setState((s) => ({ ...s, isReady: true }));
        onDoModule();
      }
      if (
        loan.borrower_cash_in_cash_out_method_account_number === null &&
        timer < MAX_TIMEOUT_MILLISECONDS
      ) {
        setTimeout(() => {
          dispatch({
            type: "REQUEST_GET_LOAN",
            payload: loan.id,
          });
          setState((s) => ({
            ...s,
            timer: s.timer + TIMEOUT_MILLISECONDS,
          }));
        }, TIMEOUT_MILLISECONDS);
      }
      if (
        loan.borrower_cash_in_cash_out_method_account_number !== null &&
        (!income || (income && Object.keys(income)?.length === 0))
      ) {
        dispatch({
          type: "REQUEST_GET_DECLARED_INCOME",
          payload: {
            loanId: loan.id,
            onboardingDetailsId: participant.onboardingDetails.id,
          },
        });
        setState((s) => ({
          ...s,
          timer: s.timer + TIMEOUT_MILLISECONDS,
        }));
      }
    }
  };

  const onSkipModule = () => {
    dispatch({
      type: "SET_MODULE_SKIPPED",
    });
    dispatch({
      type: "SET_FLOW_LOADING",
      payload: { show: false },
    });
  };

  const onDoModule = () => {
    dispatch({
      type: "SET_FLOW_LOADING",
      payload: { show: false },
    });
    setState((s) => ({
      ...s,
      bankAccountIBAN: loan.borrower_cash_in_cash_out_method_account_number,
    }));
  };

  const onShowProcessCriticalError = () => {
    setState((s) => ({
      ...s,
      isReady: true,
      cashInCashOutMethodAccountNumberIsNull: true,
    }));
    dispatch({
      type: "SET_FLOW_LOADING",
      payload: { show: false },
    });
  };

  const processPatchAddBorrowerCashInCashOutMethod = () => {
    const cashInCashOutMethodId = uuidv4();

    setState((s) => ({
      ...s,
      cashInCashOutMethodId: cashInCashOutMethodId,
    }));
    const allDataToPatch = PatchAddBorrowerCashInCashOutMethod.dehydrate({
      ...values,
      cashInCashOutMethodId: cashInCashOutMethodId,
      bankAccountIBAN: state.bankAccountIBAN,
      participant,
    });
    dispatch({
      type: "REQUEST_PATCH_ADD_BORROWER_CASH_IN_CASH_OUT_METHOD",
      payload: allDataToPatch,
    });
  };

  const processPatchSetBorrowerCashInCashOutMethodToLoan = () => {
    const allDataToPatch = PatchSetBorrowerCashInCashOutMethodToLoan.dehydrate({
      id: loan.id,
      cashInCashOutMethodId: state.cashInCashOutMethodId,
    });
    dispatch({
      type: "REQUEST_PATCH_SET_BORROWER_CASH_IN_CASH_OUT_METHOD_TO_LOAN_AND_SEQUENCE",
      payload: allDataToPatch,
    });
  };

  const setItemStepOkAndSendOkMessage = () => {
    dispatch({
      type: "SET_FLOW_ITEM_OK",
    });
    dispatch({
      type: "SET_FLOW_LOADING",
      payload: { show: false },
    });
    dispatch({
      type: "SET_FLOW_MESSAGE",
      payload: { id: FLOW_MESSAGES.SEND_OK.id },
    });
  };

  useEffect(() => {
    const isActionTypeInLastActionsReference = (actionType) =>
      lastActionsReference?.some((element) => element.type === actionType);
    if (
      isActionTypeInLastActionsReference(
        "REQUEST_GET_DECLARED_INCOME_FULFILLED"
      )
    ) {
      onConditions();
    }
    if (
      isActionTypeInLastActionsReference(
        "REQUEST_POST_DECLARED_INCOME_FULFILLED"
      )
    ) {
      processPatchAddBorrowerCashInCashOutMethod();
    }
    if (
      isActionTypeInLastActionsReference(
        "REQUEST_PATCH_ADD_BORROWER_CASH_IN_CASH_OUT_METHOD_FULFILLED"
      )
    ) {
      processPatchSetBorrowerCashInCashOutMethodToLoan();
    }
    if (
      isActionTypeInLastActionsReference(
        "REQUEST_PATCH_SET_BORROWER_CASH_IN_CASH_OUT_METHOD_TO_LOAN_AND_SEQUENCE_FULFILLED"
      )
    ) {
      setItemStepOkAndSendOkMessage();
    }
  }, [lastActionsReferenceLastChange]);

  const onCloseMessage = () => {
    setState((s) => ({ ...s, noIncomeMessage: false }));
  };

  return noIncomeMessage ? (
    <NoIncomeMessage onCloseMessage={onCloseMessage} />
  ) : cashInCashOutMethodAccountNumberIsNull === true ? (
    <CriticalErrorMessage />
  ) : !stepGroupStepToShow ? (
    <Box
      width={"100%"}
      display={"flex"}
      justifyContent={"center"}
      flexGrow={1}
      alignItems={"center"}
    >
      <CircularProgress
        size={90}
        style={{ color: palette.secondary.main }}
        thickness={1.5}
      />
    </Box>
  ) : (
    <>
      <Box className="appear-anim" style={{ position: "absolute" }}>
        <BackButton onBack={onBack} show={backButtonVisible} />
      </Box>
      {stepGroupStepToShow ===
        DECLARED_INCOME_ON_CONDITIONS.EDIT_INCOME_FORM && (
        <EditIncomeForm
          onSubmitForm={submitStep}
          data={getStepData()}
          employmentStatus={employmentStatus}
          disableNetIncomeType={disableNetIncomeType}
          disableOthers={disableOthers}
        />
      )}
      {stepGroupStepToShow ===
        DECLARED_INCOME_ON_CONDITIONS.SET_BANK_ACCOUNT && (
        <SetBankAccount
          onSubmitForm={submitStep}
          data={getStepData()}
          recordedIBAN={state.bankAccountIBAN}
          allowInternationalBorrower={allowInternationalBorrower}
        />
      )}
    </>
  );
}

export const CriticalErrorMessage = () => {
  const { palette, zendeskCustomerSupport } = useTheme();
  const {
    state: { registry },
  } = useContext(ConfigContext);
  return (
    <>
      <Container
        style={{
          padding: "1rem 1rem",
          height: "100%",
        }}
      >
        <ResponsiveLayout>
          <Box />

          <Box
            mt={4}
            display="flex"
            flexGrow={1}
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            className="appear-anim"
          >
            <ThemedAsset
              id={assetIds.ICON_FAIL}
              width={50}
              height={50}
              style={{ color: palette.error.main }}
            />
            <Grid
              container
              display="flex"
              direction="column"
              alignItems="center"
              justifyContent="center"
              style={{ paddingTop: 20 }}
            >
              <Typography
                color={palette.text.title}
                variant={"h6"}
                className={"mb-1 transition-all"}
              >
                <strong>
                  <Text tId={"OOPS_SOMETHING_WENT_WRONG"} />
                </strong>
              </Typography>
              <Typography
                variant={"body2"}
                className={"mb-3 transition-all"}
                style={{
                  color: palette.text.primary,
                  textAlign: "center",
                }}
              >
                <Text
                  tId={
                    zendeskCustomerSupport
                      ? "COULD_NOT_FIND_YOUR_BANK_ACCOUNT_NUMBER_CONTACT_CUSTOMER_SUPPORT_CHAT"
                      : "COULD_NOT_FIND_YOUR_BANK_ACCOUNT_NUMBER"
                  }
                  dynamicDataTexts={[CONTACT_INFO_BY_REGISTRY[registry]?.phone]}
                />
              </Typography>
            </Grid>
          </Box>
          <Box />
        </ResponsiveLayout>
      </Container>
    </>
  );
};
